import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      // Was false -- meaning switching back to this tab (after using
      // another app, or another browser tab) never showed anything that
      // changed in the meantime, no matter how long ago the data was
      // fetched, until a full manual page reload. An interview someone
      // else just marked COMPLETED, a candidate another recruiter moved
      // to the next stage -- all silently stale until that reload. True
      // real-time (a push the instant something changes, no refetch
      // needed at all) would mean introducing websockets, a much bigger
      // change than this one; refetching on focus is the same "feels
      // current" result for the common case of switching back to an
      // already-open tab, at a fraction of the complexity and risk.
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Retrying a 403 or a 422 just burns the rate limit and delays the
        // error the user needs to see.
        if ([400, 401, 403, 404, 422].includes(error?.status)) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

// Query keys in one place. Invalidating "employees" from three components with
// three slightly different key arrays is how stale lists happen.
export const qk = {
  me: ["me"],
  dashboard: ["dashboard", "summary"],
  celebrations: (window) => ["dashboard", "celebrations", window],
  announcementsActive: ["announcements", "active"],
  expenseCategories: (activeOnly) => ["expenses", "categories", activeOnly],
  expenseMyClaims: (filters) => ["expenses", "my-claims", filters],
  expenseApprovals: (filters) => ["expenses", "approvals", filters],
  expenseAllClaims: (filters) => ["expenses", "all-claims", filters],
  expenseClaim: (id) => ["expenses", "claims", id],
  funds: (activeOnly) => ["funds", activeOnly],
  fundsPicker: ["funds", "picker"],
  fund: (id) => ["funds", id],
  fundLedger: (id, filters) => ["funds", id, "ledger", filters],
  fundLedgerAll: (filters) => ["funds", "ledger-all", filters],
  dashboardCalendar: (year, month) => ["dashboard", "calendar", year, month],
  employees: (filters) => ["employees", filters],
  auditLogs: (filters) => ["auditLogs", filters],
  auditLogFilters: ["auditLogs", "filters"],
  employeeStatusSummary: (filters) => ["employees", "status-summary", filters],
  employee: (id) => ["employees", id],
  employeeHistory: (id) => ["employees", id, "history"],
  agentDeviceStatus: (employeeId) => ["agent", "device", employeeId],
  myMonitoringStatus: ["agent", "my-status"],
  // Missing entirely until now -- CheckInWidget.jsx's MyDeviceManager
  // referenced qk.myDevices, which resolved to undefined at runtime (not
  // a typo of an existing key, just never defined here at all). React
  // Query treats an undefined queryKey as invalid on its own terms --
  // "queryKey needs to be an Array" -- and with no valid key to key a
  // default queryFn lookup off of either, both errors fired together,
  // every 30s (this query's own refetchInterval), for the whole time
  // anyone was checked in with the CheckInWidget mounted.
  myDevices: ["agent", "my-devices"],
  teamActivitySummary: (date) => ["agent", "team-activity-summary", date],
  agentActivitySummary: (employeeId, date) => ["agent", "activity-summary", employeeId, date],
  agentActivityTimeline: (employeeId, date) => ["agent", "activity-timeline", employeeId, date],
  productivityRules: ["agent", "productivity-rules"],
  activityAdjustmentDays: (employeeId, from, to) => ["agent", "activity-adjustments", employeeId, from, to],
  employeeTeam: (id) => ["employees", id, "team"],
  directory: (filters) => ["directory", filters],
  orgChart: ["org-chart"],
  departments: ["org", "departments"],
  designations: ["org", "designations"],
  locations: ["org", "locations"],
  shifts: ["org", "shifts"],
  orgProfile: ["org", "profile"],
  holidays: (year) => ["org", "holidays", year],
  sessions: ["auth", "sessions"],
  documents: (filters) => ["documents", filters],
  documentSummary: (employeeId) => ["documents", "summary", employeeId],
  assets: (filters) => ["assets", filters],
  myAssets: ["assets", "mine"],
  employeeAssets: (employeeId) => ["assets", "employee", employeeId],
  leaveTypes: ["leave", "types"],
  leaveBalances: (filters) => ["leave", "balances", filters],
  leaveRequests: (filters) => ["leave", "requests", filters],
  leaveRequest: (id) => ["leave", "requests", id],
  attendanceToday: ["attendance", "today"],
  attendanceList: (filters) => ["attendance", "list", filters],
  salaryStructures: (employeeId) => ["payroll", "structures", employeeId],
  payrollRuns: (filters) => ["payroll", "runs", filters],
  payrollRun: (id) => ["payroll", "runs", id],
  runPayslips: (id) => ["payroll", "runs", id, "payslips"],
  myPayslips: (filters) => ["payroll", "payslips", filters],
  mySalaryStructure: ["payroll", "my-structure"],
  performanceCycles: ["performance", "cycles"],
  performanceGoals: (filters) => ["performance", "goals", filters],
  performanceReviews: (filters) => ["performance", "reviews", filters],
  offers: (filters) => ["offers", filters],
  offer: (id) => ["offers", id],
  structureDefaultsForEmployee: (employeeId) => ["payroll", "structure-defaults", employeeId],
  salaryStructureDefaultsTemplate: () => ["payroll", "structure-defaults-template"],
  lateDeductionSettings: () => ["payroll", "late-deduction-settings"],
  publicOffer: (token) => ["public", "offer", token],
  onboarding: (employeeId) => ["onboarding", employeeId],
  onboardingList: (filters) => ["onboarding", "list", filters],
  // A single onboarding record by its own _id -- the DIRECT onboarding
  // detail page's own lookup, distinct from the by-employee/by-application
  // keys above since a DIRECT record has neither until it's converted.
  onboardingRecord: (id) => ["onboarding", "record", id],
  // The public, token-gated candidate portal for a DIRECT onboarding record
  // -- same "public" prefix convention as publicOffer above.
  onboardingPortal: (token) => ["public", "onboarding", token],
};
